import { currentLang, t } from './i18n';
import { userTimezone } from './state';
import type { Goal } from './types';

// Stays until the next submit clears it, so a message read late is still there.
export function showMsg(id, text) {
  const el = document.getElementById(id);
  el.textContent = text;
  el.classList.add('show');
}

export function hideMsg(id) {
  document.getElementById(id)?.classList.remove('show');
}

export type PasswordResetHash =
  | { kind: 'recovery'; tokenHash: string }
  | { kind: 'invalid' };

// Supabase returns an expired or used reset link as `#error=...&error_code=otp_expired`.
export function classifyPasswordResetHash(hash: string): PasswordResetHash | null {
  if (!hash) return null;
  const params = new URLSearchParams(hash.replace(/^#reset-password/, '').replace(/^#/, ''));
  if (params.get('error') || params.get('error_code')) return { kind: 'invalid' };
  const tokenHash = params.get('token_hash');
  if (tokenHash && params.get('type') === 'recovery') {
    return { kind: 'recovery', tokenHash };
  }
  if (params.get('type') === 'recovery' || hash.startsWith('#reset-password')) return { kind: 'invalid' };
  return null;
}

export type ConfirmEmailHash =
  | { kind: 'signup'; tokenHash: string; email: string }
  | { kind: 'invalid' };

// #2793: the signup confirmation mail links to `#confirm-email?token_hash=…&type=signup&email=…`.
export function classifyConfirmEmailHash(hash: string): ConfirmEmailHash | null {
  if (!hash || !hash.startsWith('#confirm-email')) return null;
  const params = new URLSearchParams(hash.replace(/^#confirm-email\??/, ''));
  const tokenHash = params.get('token_hash');
  if (tokenHash && params.get('type') === 'signup' && !params.get('error')) {
    return { kind: 'signup', tokenHash, email: params.get('email') ?? '' };
  }
  return { kind: 'invalid' };
}

export function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

export function parseDeadlineDate(dlStr) {
  if (!dlStr) return null;
  const match = dlStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
}

// Making a date format is slow, and the deadline counts ask for today once per
// date of every goal; one format is kept per time zone.
let todayFormat: { tz: string; format: Intl.DateTimeFormat } | null = null;

export function getLocalToday() {
  const tz = userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (todayFormat?.tz !== tz) {
    todayFormat = { tz, format: new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }) };
  }
  const parts = todayFormat.format.format(new Date());
  const [y, m, d] = parts.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function classifyDeadlineFilterBucket(deadline) {
  if (!deadline) return 'none';

  const dl = parseDeadlineDate(deadline);
  if (!dl) return 'none';

  const today = getLocalToday();
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfterTomorrow = new Date(tomorrow); dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);
  const endOfWeek = new Date(today); endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  if (dl < today) return 'overdue';
  if (dl < tomorrow) return 'today';
  if (dl < dayAfterTomorrow) return 'tomorrow';
  if (dl <= endOfWeek) return 'week';
  if (dl <= endOfMonth) return 'month';
  return 'later';
}

export function formatDateJP(str) {
  if (!str) return null;
  const d = parseDeadlineDate(str);
  if (!d) return str;
  if (currentLang === 'en') {
    const wds = t('weekdays');
    return `${wds[d.getDay()]} ${d.getMonth() + 1}/${d.getDate()}`;
  }
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

export function formatDeadlineBucketLabel(dateStr) {
  if (!dateStr) return '期日なし';
  const d = parseDeadlineDate(dateStr);
  if (!d) return '期日なし';
  const today = getLocalToday();
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const dayAfterTomorrow = new Date(tomorrow); dayAfterTomorrow.setDate(tomorrow.getDate() + 1);
  const endOfWeek = new Date(today); endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  if (d < today) return '期限超過';
  if (d < tomorrow) return '今日';
  if (d < dayAfterTomorrow) return '明日';
  if (d <= endOfWeek) return '今週';
  if (d <= endOfMonth) return '今月';
  return '来月以降';
}

export function formatDeadlineLabel(str: string, hasTime?: boolean) {
  if (!str) return '';
  const target = parseDeadlineDate(str);
  if (!target) return str;
  const today = getLocalToday();
  const diff = Math.round((target.getTime() - today.getTime()) / 86400000);
  let label;
  if (diff === 0) label = t('today');
  else if (diff === 1) label = t('tomorrow');
  else label = `${target.getMonth() + 1}/${target.getDate()}`;
  if (hasTime) {
    const d = new Date(str);
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    label += ` ${h}:${m}`;
  }
  return label;
}

export function isOverdue(str) {
  if (!str) return false;
  const target = parseDeadlineDate(str);
  if (!target) return false;
  return target < getLocalToday();
}

/**
 * #2681: the deadline a task shows. A task that inherits the Goal deadline
 * has no date of its own and shows none; the Goal row carries the deadline.
 * A deadline the principal set stays, and `afterGoal` marks one that now
 * falls after the Goal deadline (the Goal was moved earlier).
 */
export function subtaskDeadlineView(
  st: { deadline: string | null; deadline_inherited?: boolean },
  goalDeadline: string | null | undefined,
): { deadline: string | null; afterGoal: boolean } {
  if (st.deadline_inherited !== false || !st.deadline) return { deadline: null, afterGoal: false };
  const afterGoal = !!goalDeadline && new Date(st.deadline).getTime() > new Date(goalDeadline).getTime();
  return { deadline: st.deadline, afterGoal };
}

type GoalDeadlineFields = Pick<Goal, 'deadline' | 'deadline_has_time' | 'dates'>;

// #2320: a Goal's deadline is read from `dates.deadline`, which the server's
// one reader fills in the profile timezone. The raw column is only the
// fallback for a readback that does not carry `dates`.
export function goalDeadlineDate(goal: GoalDeadlineFields): string | null {
  return goal.dates?.deadline?.date ?? goal.deadline ?? null;
}

export function isGoalOverdue(goal: GoalDeadlineFields): boolean {
  const read = goal.dates?.deadline;
  return read ? read.overdue : isOverdue(goal.deadline);
}

/**
 * #3064: no deadline was read from the source; the date is the principal's
 * default (`unread_deadline_default`). Shown with 「仮」 (word, not color
 * alone) so it is not taken for a deadline someone stated.
 */
export function isGoalDeadlineProvisional(goal: Pick<Goal, 'dates'>): boolean {
  return goal.dates?.deadline?.provisional === true;
}

export function provisionalDeadlineCopy(lang = currentLang): { tag: string; title: string; aria: string } {
  return lang === 'en'
    ? { tag: 'TBC', title: 'Tentative deadline: the message gave none, so your default from Settings is used.', aria: 'Tentative deadline' }
    : { tag: '仮', title: '仮の期限です。元の連絡に期限がなかったので、設定の既定で置いた日付です。', aria: '仮の期限' };
}

export function formatGoalDeadlineLabel(goal: GoalDeadlineFields): string {
  const read = goal.dates?.deadline;
  if (!read) return formatDeadlineLabel(goal.deadline, goal.deadline_has_time);
  let label = formatDeadlineLabel(read.date);
  if (read.has_time) {
    const time = new Intl.DateTimeFormat('en-GB', { timeZone: read.timezone, hour: '2-digit', minute: '2-digit', hour12: false })
      .format(new Date(read.at));
    label += ` ${time}`;
  }
  return label;
}

// #2320: the Goal's event time (the linked calendar event, else the time the
// source states), read by the server's one reader in the profile timezone.
export function formatGoalEventLabel(goal: Pick<Goal, 'dates'>): string | null {
  const event = goal.dates?.event;
  if (!event) return null;
  let label = formatDeadlineLabel(event.date);
  // #3060: a date the source gave without a start time is shown as the date alone.
  if (event.all_day || event.has_time === false) return label;
  const clock = (instant: string) => new Intl.DateTimeFormat('en-GB', { timeZone: event.timezone, hour: '2-digit', minute: '2-digit', hour12: false })
    .format(new Date(instant));
  label += ` ${clock(event.start_at)}`;
  if (event.end_at && Date.parse(event.end_at) > Date.parse(event.start_at)) label += `–${clock(event.end_at)}`;
  return label;
}

export function sortByDeadline(goals) {
  return goals.slice().sort((a, b) => {
    const ea = goalDeadlineDate(a);
    const eb = goalDeadlineDate(b);
    const da = ea ? (parseDeadlineDate(ea)?.getTime() ?? Infinity) : Infinity;
    const db = eb ? (parseDeadlineDate(eb)?.getTime() ?? Infinity) : Infinity;
    return da - db;
  });
}

export function itemMatchesDeadlineFilter(deadline, filter) {
  if (filter === 'all') return true;
  return classifyDeadlineFilterBucket(deadline) === filter;
}
